import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import GlitchImageLogo from "./GlitchImageLogo";
import { useAuth } from "../context/AuthContext"; // <-- added

export default function Header() {
  const { user, isAdmin } = useAuth(); // <-- added
  const [open, setOpen] = useState(false);
  const headerRef = useRef(null);
  const panelRef = useRef(null);
  const buttonRef = useRef(null);
  const [offsetTop, setOffsetTop] = useState(70);

  // Compute account link/label
  const accountPath = user ? (isAdmin ? "/admin" : "/dashboard") : "/login";
  const accountLabel = user ? (isAdmin ? "Admin" : "Account") : "Sign In";

  useLayoutEffect(() => {
    const measure = () => {
      const h = headerRef.current?.getBoundingClientRect().height || 70;
      setOffsetTop(h);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      const p = panelRef.current;
      const b = buttonRef.current;
      if (!p) return;
      const inPanel = p.contains(e.target);
      const onButton = b && b.contains(e.target);
      if (!inPanel && !onButton) setOpen(false);
    };
    window.addEventListener("pointerdown", onDown);
    return () => window.removeEventListener("pointerdown", onDown);
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const onResize = () => {
      if (window.matchMedia("(min-width: 768px)").matches) setOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const MobileLink = ({ to, children }) => (
    <Link
      to={to}
      onClick={() => setOpen(false)}
      className="block w-full px-4 py-3 rounded-lg text-base font-medium text-white hover:text-[#D4FF00] hover:bg-white/5 transition-colors"
    >
      {children}
    </Link>
  );

  return (
    <header
      ref={headerRef}
      className="sticky top-0 z-50 border-b border-white/5 bg-gradient-to-b from-black/85 via-black/35 to-transparent backdrop-blur-md"
    >
      <div className="container-x flex items-center justify-between min-h-[70px]">
        <Link
          to="/"
          className="font-black tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-[#D4FF00] to-white"
        >
          <GlitchImageLogo
            src="/images/logotest.png"
            alt="Revelstoke Gaming Cafe"
            className="w-60 md:w-60 h-auto"
            duration={1600}
            oncePerSession={true}
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6 text-sm text-neutral-400">
          <Link to="/pricing" className="text-white hover:text-[#D4FF00] transition-colors">
            Pricing
          </Link>

          <Link to={{ pathname: "/", hash: "#faq" }} className="text-white hover:text-[#D4FF00] transition-colors">
            FAQ
          </Link>

          <Link to="/contact" className="text-white hover:text-[#D4FF00] transition-colors">
            Contact
          </Link>

          {/* NEW: Login/Account/Admin link */}
          <Link
            to={accountPath}
            className="text-white/90 hover:text-[#D4FF00] transition-colors border border-accent hover:border-[#D4FF00]/60 rounded-lg px-4 py-2.5 rounded-3xl"
          >
            {accountLabel}
          </Link>

          {/* Primary CTA: Book Now (kept in focus) */}
          <Link
            to="/booking"
            className="
              btn btn-accent font-semibold transition px-3 duration-150 ease-linear
              focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#D4FF00]/40
              ring-2 ring-[#D4FF00]/50 hover:bg-black hover:text-white
              shadow-[0_0_24px_rgba(212,255,0,0.32)]
            "
            aria-label="Book a PC now"
          >
            Book Now
          </Link>
        </nav>

        {/* Mobile toggle */}
        <button
          ref={buttonRef}
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border border-[#1a1a1a] bg-neutral-900"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="mobile-panel"
          onClick={() => setOpen((o) => !o)}
        >
          <span className="sr-only">Menu</span>
          <div className="relative w-5 h-5">
            <span className={`absolute inset-x-0 top-1 block h-0.5 bg-white transition-transform ${open ? "translate-y-2 rotate-45" : ""}`} />
            <span className={`absolute inset-x-0 top-1/2 -translate-y-1/2 block h-0.5 bg-white transition-opacity ${open ? "opacity-0" : "opacity-100"}`} />
            <span className={`absolute inset-x-0 bottom-1 block h-0.5 bg-white transition-transform ${open ? "-translate-y-2 -rotate-45" : ""}`} />
          </div>
        </button>
      </div>

      {/* Mobile panel */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id="mobile-panel"
            ref={panelRef}
            role="dialog"
            aria-modal="false"
            className="md:hidden fixed inset-x-0 z-[55]"
            style={{ top: offsetTop }}
            initial={false}
          >
            <motion.div
              initial={{ y: -12, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: -12, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className="container-x"
            >
              <div
                className="
                  mt-2 rounded-2xl border border-[#1a1a1a] bg-[#0b0b0be6]
                  backdrop-blur-xl shadow-[0_10px_30px_rgba(212,255,0,.10),0_2px_8px_rgba(0,0,0,.6)]
                  ring-1 ring-white/5
                "
              >
                <nav className="py-2">
                  <MobileLink to="/pricing">Pricing</MobileLink>
                  <MobileLink to="/contact">Contact</MobileLink>
                  <MobileLink to={{ pathname: "/", hash: "#faq" }}>FAQ</MobileLink>

                  {/* NEW: login/account/admin on mobile */}
                  <MobileLink to={accountPath}>{accountLabel}</MobileLink>

                  <div className="p-3">
                    {/* Primary CTA: Book Now emphasized */}
                    <Link
                      to="/booking"
                      onClick={() => setOpen(false)}
                      className="
                        block text-center btn btn-accent w-full
                        hover:bg-black hover:text-white transition-colors
                        focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#D4FF00]/40
                        ring-2 ring-[#D4FF00]/50 shadow-[0_0_24px_rgba(212,255,0,0.32)]
                      "
                    >
                      Book Now
                    </Link>
                  </div>
                </nav>

                <div className="px-4 pb-3 text-sm text-neutral-400 flex items-center justify-between">
                  <span>Open Daily • 11:00am – 5:00pm</span>
                  <a href="mailto:info@revytech.ca" className="hover:text-[#D4FF00] transition-colors">
                    info@revytech.ca
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
